/**
 * THE GENERIC FORM ADAPTER (EXPERT-ROOMS.md §7, E8 X3) — renders any declared form (``[{key, label, type, required?,
 * options?, help?}]``) and shows the server's per-field errors. Types: text · long-text · email · number · choice ·
 * multi-choice · yes-no · date · reference. The SERVER validates (the same rules); this only collects. Never a secret
 * (the server refuses one and says why). Reusable: offerings today, onboarding questions / any structured ask later.
 */
import React from 'react';

const input = 'mt-0.5 block w-full rounded-md border border-gray-300 px-2 py-1 text-xs';

function Field({ f, value, error, onChange, disabled }) {
  const id = `form-${f.key}`;
  const set = (v) => onChange(f.key, v);
  let control;
  if (f.type === 'long-text') {
    control = <textarea id={id} rows={3} value={value ?? ''} disabled={disabled} className={input}
      onChange={(e) => set(e.target.value)} />;
  } else if (f.type === 'choice') {
    control = (
      <select id={id} value={value ?? ''} disabled={disabled} className={input} onChange={(e) => set(e.target.value)}>
        <option value="">Choose…</option>
        {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (f.type === 'multi-choice') {
    const cur = Array.isArray(value) ? value : [];
    control = (
      <div className="mt-0.5 flex flex-wrap gap-2" role="group" aria-labelledby={`${id}-label`}>
        {(f.options || []).map((o) => (
          <label key={o} className="flex items-center gap-1 text-xs">
            <input type="checkbox" checked={cur.includes(o)} disabled={disabled}
              onChange={(e) => set(e.target.checked ? [...cur, o] : cur.filter((x) => x !== o))} />{o}
          </label>
        ))}
      </div>
    );
  } else if (f.type === 'yes-no') {
    control = (
      <div className="mt-0.5 flex gap-3 text-xs" role="radiogroup" aria-labelledby={`${id}-label`}>
        {[['Yes', true], ['No', false]].map(([l, v]) => (
          <label key={l} className="flex items-center gap-1">
            <input type="radio" name={id} checked={value === v} disabled={disabled} onChange={() => set(v)} />{l}
          </label>
        ))}
      </div>
    );
  } else {
    const type = { email: 'email', number: 'number', date: 'date' }[f.type] || 'text';
    control = <input id={id} type={type} value={value ?? ''} disabled={disabled} className={input}
      onChange={(e) => set(e.target.value)} />;
  }
  return (
    <div data-testid={`form-field-${f.key}`}>
      <label id={`${id}-label`} htmlFor={id} className="block text-[11px] font-medium text-gray-700">
        {f.label}{f.required ? <span className="text-rose-600"> *</span> : null}
        {/* §17 — the ONE kind of value that leaves the room as-is: the team declared it needs it */}
        {f.shared ? (
          <span className="ml-1 rounded bg-amber-50 px-1 py-px text-[9px] font-medium text-amber-800"
            data-testid={`form-shared-${f.key}`} title="The team needs this exact value to do the work — it is sent as you enter it">
            shared with the team
          </span>
        ) : null}
      </label>
      {control}
      {f.help ? <p className="text-[10px] text-gray-500">{f.help}</p> : null}
      {error ? <p className="text-[11px] text-rose-600" data-testid={`form-error-${f.key}`}>{error}</p> : null}
    </div>
  );
}

export default function FormCard({ form, values, errors, onChange, disabled = false }) {
  const v = values || {};
  const e = errors || {};
  return (
    <div className="space-y-2" data-testid="form-card">
      {/* THE ROOM BOUNDARY (EXPERT-ROOMS §17): say what leaves before anyone submits */}
      {(form || []).length ? (
        <p className="text-[10px] text-gray-500" data-testid="form-boundary">
          Account numbers, emails and other identifiers you enter leave this room as placeholders
          {(form || []).some((f) => f.shared) ? ' — except fields marked "shared with the team"' : ''}. Secrets are never sent.
        </p>
      ) : null}
      {(form || []).map((f) => (
        <Field key={f.key} f={f} value={v[f.key]} error={e[f.key]} disabled={disabled}
          onChange={(k, val) => onChange?.({ ...v, [k]: val })} />
      ))}
    </div>
  );
}
